import type { WozCaseKadaster } from '@footsteps/types';
import type { ChatMessage } from '@footsteps/shared';
import type { WozCaseStatus, WozCaseComplaintMode, WozCasePersistenceMode } from '../woz-case.types';
import { WozCaseComplaintEntity } from './woz-case-complaint.entity';
import { WozCaseFlowEntity } from './woz-case-flow.entity';
import { WozCaseMessageEntity } from './woz-case-message.entity';

/** Constructor input — accepts both entity instances and plain JSON objects. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WozCaseEntityInput = Omit<Partial<WozCaseEntity>, 'complaints' | 'flow' | 'messages'> & {
  complaints?: any[];
  flow?: any;
  messages?: any[];
};

/**
 * Domain entity mirroring the `cases` table with runtime-only additions.
 * This is the single source of truth used throughout the frontend.
 */
export class WozCaseEntity {
  id: string | null;
  userId: string | null;
  addressPointId: string | null;
  addressCity: string | null;
  addressStreet: string | null;
  addressHouseNumber: number | null;
  addressHouseLetter: string | null;
  addressHouseNumberAddition: string | null;
  addressPostalCode: string | null;
  complaintMode: WozCaseComplaintMode | null;
  status: WozCaseStatus;
  createdAt: string;
  updatedAt: string;
  complaints: WozCaseComplaintEntity[];
  flow: WozCaseFlowEntity | null;
  messages: WozCaseMessageEntity[];
  source: WozCasePersistenceMode;
  kadaster: WozCaseKadaster | null;
  signatureData: string | null;
  dateSigned: string | null;
  classificationRationale: string | null;
  originalComplaint: string | null;
  originalComplaintFormatted: string | null;

  constructor(data: WozCaseEntityInput = {}) {
    const now = new Date().toISOString();

    this.id = data.id ?? null;
    this.userId = data.userId ?? null;
    this.addressPointId = data.addressPointId ?? null;
    this.addressCity = data.addressCity ?? null;
    this.addressStreet = data.addressStreet ?? null;
    this.addressHouseNumber = data.addressHouseNumber ?? null;
    this.addressHouseLetter = data.addressHouseLetter ?? null;
    this.addressHouseNumberAddition = data.addressHouseNumberAddition ?? null;
    this.addressPostalCode = data.addressPostalCode ?? null;
    this.complaintMode = data.complaintMode ?? null;
    this.status = data.status ?? 'open';
    this.createdAt = data.createdAt ?? now;
    this.updatedAt = data.updatedAt ?? now;

    this.complaints = (data.complaints ?? []).map((complaint: any) =>
      complaint instanceof WozCaseComplaintEntity ? complaint : new WozCaseComplaintEntity(complaint)
    );

    this.flow = data.flow
      ? data.flow instanceof WozCaseFlowEntity
        ? data.flow
        : new WozCaseFlowEntity(data.flow)
      : null;

    this.messages = (data.messages ?? []).map((message) =>
      message instanceof WozCaseMessageEntity ? message : new WozCaseMessageEntity(message)
    );

    this.source = data.source ?? 'local';
    this.kadaster = data.kadaster ?? null;
    this.signatureData = data.signatureData ?? null;
    this.dateSigned = data.dateSigned ?? null;
    this.classificationRationale = data.classificationRationale ?? null;
    this.originalComplaint = data.originalComplaint ?? null;
    this.originalComplaintFormatted = data.originalComplaintFormatted ?? null;
  }

  /** Create a brand-new local case with sensible defaults. */
  static create(): WozCaseEntity {
    return new WozCaseEntity({ source: 'local', status: 'open' });
  }

  get isLocal(): boolean {
    return this.source === 'local';
  }

  get isDatabase(): boolean {
    return this.source === 'database';
  }

  get displayAddress(): string {
    const parts: string[] = [];
    if (this.addressStreet) {
      let numberPart = '';
      if (this.addressHouseNumber != null) numberPart = String(this.addressHouseNumber);
      if (this.addressHouseLetter) numberPart += this.addressHouseLetter;
      if (this.addressHouseNumberAddition) numberPart += `-${this.addressHouseNumberAddition}`;
      parts.push(`${this.addressStreet} ${numberPart}`.trim());
    }
    const locale = [this.addressPostalCode, this.addressCity].filter(Boolean).join(' ');
    if (locale) parts.push(locale);
    return parts.join(', ');
  }

  /** Serialise to a plain JSON object (full in-memory shape). */
  toJSON() {
    return {
      id: this.id,
      userId: this.userId,
      source: this.source,
      addressPointId: this.addressPointId,
      addressCity: this.addressCity,
      addressStreet: this.addressStreet,
      addressHouseNumber: this.addressHouseNumber,
      addressHouseLetter: this.addressHouseLetter,
      addressHouseNumberAddition: this.addressHouseNumberAddition,
      addressPostalCode: this.addressPostalCode,
      complaintMode: this.complaintMode,
      status: this.status,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      complaints: this.complaints.map((complaint) => complaint.toJSON()),
      flow: this.flow?.toJSON() ?? null,
      messages: this.messages.map((message) => message.toJSON()),
      kadaster: this.kadaster,
      signatureData: this.signatureData,
      dateSigned: this.dateSigned,
      classificationRationale: this.classificationRationale,
      originalComplaint: this.originalComplaint,
      originalComplaintFormatted: this.originalComplaintFormatted,
    };
  }

  /**
   * Serialise for localStorage persistence.
   * Strips transient message props (thinking, removing, canRevert)
   * and non-serializable icon references from action items.
   */
  toStorage() {
    const json = this.toJSON();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    json.messages = (json.messages as any[])
      .filter((message: any) => message.content && message.content.type !== 'thinking')
      .map((message: any) => {
        if (message.content?.type === 'actions' && Array.isArray(message.content.actions)) {
          return {
            ...message,
            content: {
              ...message.content,
              actions: message.content.actions.map(({ icon, ...rest }: any) => rest),
            },
          };
        }
        return message;
      });

    return json;
  }

  /** Get messages as ChatMessage[] for the chatbot viewmodel. */
  getChatMessages(): ChatMessage[] {
    return this.messages.map((message) => message.toJSON());
  }
}
